import { useCallback, ReactNode, useState, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormSection } from '../FormSection';
import CheckBox from '../core/Checkbox';
import Button from '../core/Button';
import Input from '../core/Input';
import Fuse from 'fuse.js';
import IconButton from '../core/IconButton';
import { IconXmark } from '@/icons';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { collator } from '@/utils/collator';
import { Spinner } from '@/components/core/Spinner';

interface CheckboxSectionProps {
  title: ReactNode;
  description: ReactNode;
  items: {
    id: string;
    title: string;
    subtitle?: string;
  }[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  editMode: boolean;
  onClickButton: () => void;
  canSeeButton?: boolean;
  editActions?: ReactNode;
  isLoading?: boolean;
  /** Selected ids that cannot be cleared. New ids can still be added. */
  lockedIds?: string[];
  lockedTooltip?: string;
  lockedDescribedBy?: string;
}

export const CheckboxSection = ({
  title,
  description,
  items,
  selectedIds,
  onSelectionChange,
  editMode,
  onClickButton,
  canSeeButton,
  editActions,
  isLoading,
  lockedIds = [],
  lockedTooltip,
  lockedDescribedBy,
}: CheckboxSectionProps) => {
  const [ref] = useAutoAnimateConditional<HTMLUListElement>();
  const [search, setSearch] = useState('');

  const fuse = useMemo(
    () =>
      new Fuse(items || [], {
        keys: ['title'],
        ignoreLocation: true,
        isCaseSensitive: false,
        threshold: 0.1,
      }),
    [items],
  );

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) =>
      collator.compare(a.title.toLowerCase(), b.title.toLowerCase()),
    );
  }, [items]);

  const searchResult = useMemo(() => {
    if (!search) {
      return sortedItems;
    }
    return fuse.search(search).map((result) => result.item);
  }, [fuse, search, sortedItems]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange([
      ...new Set([...lockedIds, ...searchResult.map((item) => item.id)]),
    ]);
  }, [lockedIds, searchResult, onSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    onSelectionChange(lockedIds);
  }, [lockedIds, onSelectionChange]);

  useEffect(() => {
    setSearch('');
  }, [editMode]);

  return (
    <FormSection
      title={title}
      onClickButton={onClickButton}
      editMode={editMode}
      canSeeButton={canSeeButton}
      editActions={editActions}
    >
      <div>{description}</div>
      {editMode && (
        <div className="mb-4 flex flex-col gap-2">
          <div className="flex gap-1">
            <Input
              placeholder="Sök"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <IconButton
              aria-label="Rensa sökning"
              icon={<IconXmark />}
              onClick={() => setSearch('')}
              variant="ghost"
            />
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleSelectAll}>
              <FormattedMessage defaultMessage="Markera alla" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
              <FormattedMessage defaultMessage="Avmarkera alla" />
            </Button>
          </div>
        </div>
      )}
      {isLoading && (
        <div className="flex h-48 w-full items-center justify-center">
          <Spinner className="size-4 animate-spin" />
        </div>
      )}
      <ul ref={ref} className="flex flex-col gap-2">
        {searchResult.map((item) => {
          const isSelected = selectedIds.includes(item.id);
          const isLocked = lockedIds.includes(item.id);
          const label = item.subtitle
            ? `${item.title} (${item.subtitle})`
            : item.title;
          return (
            <li key={item.id}>
              <CheckBox
                id={item.id}
                label={label}
                checked={isSelected || isLocked}
                className={isSelected && !editMode ? '!bg-primary-500' : ''}
                labelClassName={
                  isSelected && !editMode ? '[&_label]:!text-text-800' : ''
                }
                toolTip={isLocked ? lockedTooltip : undefined}
                aria-describedby={
                  isLocked && lockedDescribedBy ? lockedDescribedBy : undefined
                }
                onClick={() => {
                  if (isLocked) return;
                  if (isSelected) {
                    onSelectionChange(
                      selectedIds.filter((id) => id !== item.id),
                    );
                  } else {
                    onSelectionChange([...selectedIds, item.id]);
                  }
                }}
                disabled={!editMode || isLocked}
              />
            </li>
          );
        })}
      </ul>
    </FormSection>
  );
};
